import { ContentWidth } from "@faire/web-api/indigofair/data/ContentWidth";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { WidthConstraint } from "@faire/web-api/indigofair/data/WidthConstraint";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";

import { MAX_CONTENT_WIDTH } from "@faire/retailer/features/Marketplace/styleConsts";

import { ComponentLoggingEventParams } from "../types";

import { numberToPixel } from "./conversions";

export const getContentWidthCss = (
  width: keyof typeof ContentWidth | undefined,
  loggingData: ComponentLoggingEventParams
): React.CSSProperties => {
  return {
    maxWidth: getContentWidth(
      getServerContentWidthToClientSideContentWidth(width) ??
        getContentWidthFallback(loggingData)
    ),
  };
};

// TODO: https://fairewholesale.atlassian.net/browse/FD-208072
// All of the conversion from server side to client side content width should be done in the server side
enum ClientSideContentWidth {
  HOMEPAGE_STANDARD,
  CATEGORY_PAGE_STANDARD,
  XLARGE,
  XXLARGE,
}

const contentWidthMap: Record<ClientSideContentWidth, number> = {
  [ClientSideContentWidth.HOMEPAGE_STANDARD]: 1280,
  [ClientSideContentWidth.CATEGORY_PAGE_STANDARD]: MAX_CONTENT_WIDTH,
  [ClientSideContentWidth.XLARGE]: MIN_WIDTH_BREAKPOINTS.XLARGE,
  [ClientSideContentWidth.XXLARGE]: MIN_WIDTH_BREAKPOINTS.XXLARGE,
};

const getContentWidth = (width: ClientSideContentWidth): string => {
  return numberToPixel(contentWidthMap[width]) ?? "auto";
};

const getServerContentWidthToClientSideContentWidth = (
  width: keyof typeof ContentWidth | undefined
): ClientSideContentWidth | undefined => {
  switch (width) {
    case ContentWidth.DESKTOP_STANDARD:
      return ClientSideContentWidth.HOMEPAGE_STANDARD;
    default:
      return undefined;
  }
};

// TODO: https://fairewholesale.atlassian.net/browse/FD-208072
// Fallback logic should be removed once it is prevalent on the server
const getContentWidthFallback = (
  loggingData: ComponentLoggingEventParams
): ClientSideContentWidth => {
  if (loggingData.view_display_page === "HOMEPAGE") {
    return ClientSideContentWidth.HOMEPAGE_STANDARD;
  }

  if (loggingData.view_display_page === "CATEGORY_PAGE") {
    if (
      loggingData.component_type ===
      ILayoutElement.Type.MULTI_IMAGE_TITLE_BANNER
    ) {
      return ClientSideContentWidth.XXLARGE;
    }

    if (loggingData.component_type === ILayoutElement.Type.HORIZONTAL_LINE) {
      return ClientSideContentWidth.XLARGE;
    }

    return ClientSideContentWidth.CATEGORY_PAGE_STANDARD;
  }

  return ClientSideContentWidth.HOMEPAGE_STANDARD;
};

export const convertServerWidthConstraintToCSS = (
  widthConstraint: keyof typeof WidthConstraint | undefined
) => {
  switch (widthConstraint) {
    case WidthConstraint.FULL_WIDTH:
      return "100%";
    case WidthConstraint.THREE_QUARTERS_WIDTH:
      return "75%";
    case WidthConstraint.TWO_THIRDS_WIDTH:
      return "66%";
    default:
      return undefined;
  }
};
